import React, { useEffect, useMemo, useState } from "react";
import {
    CAlert,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CDropdown,
    CDropdownMenu,
    CDropdownToggle,
    CForm,
    CFormCheck,
    CFormSelect,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from "@coreui/react";

import { fetchReports } from "../api/reports";
import { createMontlyReport, fetchMontlyReports } from "../api/montlyReports";
import { t } from "../i18n";

export default function DashboardPage() {
    const currentYear = new Date().getFullYear();

    const [reports, setReports] = useState([]);
    const [montlyReports, setMontlyReports] = useState([]);
    const [selectedReportIds, setSelectedReportIds] = useState([]);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const comparison = useMemo(
        () => buildExpenseComparison(montlyReports, selectedYear),
        [montlyReports, selectedYear]
    );
    const comparisonYears = useMemo(
        () => buildComparisonYears(montlyReports, currentYear),
        [montlyReports, currentYear]
    );

    useEffect(() => {
        let isMounted = true;

        async function loadDashboard() {
            try {
                const [loadedReports, loadedMontlyReports] = await Promise.all([
                    fetchReports(),
                    fetchMontlyReports(),
                ]);

                if (isMounted) {
                    setReports(loadedReports);
                    setMontlyReports(loadedMontlyReports);
                    setError(null);
                }
            } catch (loadError) {
                if (isMounted) {
                    setError(loadError.message);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        loadDashboard();

        return () => {
            isMounted = false;
        };
    }, []);

    function toggleReportSelection(reportId) {
        setSelectedReportIds((currentReportIds) => {
            if (currentReportIds.includes(reportId)) {
                return currentReportIds.filter((currentReportId) => currentReportId !== reportId);
            }

            return [...currentReportIds, reportId];
        });
    }

    async function handleSubmit(event) {
        event.preventDefault();

        setError(null);
        setSubmitting(true);

        try {
            const updatedMontlyReports = await createMontlyReport({
                reportIds: selectedReportIds,
            });

            setMontlyReports(updatedMontlyReports);
            setSelectedReportIds([]);
        } catch (createError) {
            setError(createError.message);
        } finally {
            setSubmitting(false);
        }
    }

    function formatAmount(amount) {
        return new Intl.NumberFormat("lt-LT", {
            style: "currency",
            currency: "EUR",
        }).format(Number(amount));
    }

    const canSubmit = selectedReportIds.length > 0 && !submitting;
    const reportsDropdownText =
        selectedReportIds.length > 0
            ? t("dashboard.selectedReports", { count: selectedReportIds.length })
            : t("dashboard.chooseReports");

    return (
        <>
            <div className="mb-4">
                <h1>{t("dashboard.title")}</h1>
                <p className="text-body-secondary mb-0">
                    {t("dashboard.description")}
                </p>
            </div>

            <CCard className="mb-4">
                <CCardHeader>
                    <strong>{t("dashboard.create")}</strong>
                </CCardHeader>

                <CCardBody>
                    {error && (
                        <CAlert color="danger">
                            {error}
                        </CAlert>
                    )}

                    <CForm onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
                                <label className="form-label mb-0">
                                    {t("dashboard.reportsLabel")}
                                </label>
                            </div>

                            <CDropdown autoClose="outside">
                                <CDropdownToggle
                                    color="secondary"
                                    disabled={loading || submitting || reports.length === 0}
                                    variant="outline"
                                >
                                    {reportsDropdownText}
                                </CDropdownToggle>

                                {reports.length === 0 ? (
                                    <div className="text-body-secondary p-3">
                                        {t("dashboard.noReports")}
                                    </div>
                                ) : (
                                    <CDropdownMenu
                                        className="p-0 overflow-auto"
                                        style={{ maxHeight: "280px", minWidth: "320px" }}
                                    >
                                        {reports.map((report) => {
                                            const reportId = String(report.id);
                                            const checked = selectedReportIds.includes(reportId);

                                            return (
                                                <label
                                                    className="dropdown-item d-flex align-items-center gap-3 mb-0"
                                                    key={report.id}
                                                >
                                                    <CFormCheck
                                                        checked={checked}
                                                        disabled={loading || submitting}
                                                        onChange={() => toggleReportSelection(reportId)}
                                                    />

                                                    <span className="fw-semibold">
                                                        {report.title}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </CDropdownMenu>
                                )}
                            </CDropdown>
                        </div>

                        <CButton color="primary" type="submit" disabled={!canSubmit}>
                            {submitting ? t("dashboard.creating") : t("dashboard.createButton")}
                        </CButton>
                    </CForm>
                </CCardBody>
            </CCard>

            <CCard>
                <CCardHeader>
                    <strong>{t("dashboard.comparison")}</strong>
                </CCardHeader>

                <CCardBody>
                    {loading && (
                        <div className="d-flex align-items-center gap-2 text-body-secondary">
                            <CSpinner size="sm" />
                            {t("dashboard.loading")}
                        </div>
                    )}

                    {!loading && reports.length === 0 && (
                        <p className="text-body-secondary mb-0">
                            {t("dashboard.noReports")}
                        </p>
                    )}

                    {!loading && reports.length > 0 && montlyReports.length === 0 && (
                        <p className="text-body-secondary mb-0">
                            {t("dashboard.empty")}
                        </p>
                    )}

                    {!loading && montlyReports.length > 0 && (
                        <>
                            <div className="mb-3" style={{ maxWidth: "220px" }}>
                                <CFormSelect
                                    label={t("dashboard.year")}
                                    value={selectedYear}
                                    onChange={(event) => setSelectedYear(Number(event.target.value))}
                                >
                                    {comparisonYears.map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </CFormSelect>
                            </div>

                            {comparison.expenses.length === 0 ? (
                                <p className="text-body-secondary mb-0">
                                    {t("dashboard.emptyYear")}
                                </p>
                            ) : (
                                <CTable responsive hover className="mb-0 align-middle">
                                    <CTableHead>
                                        <CTableRow>
                                            <CTableHeaderCell scope="col">
                                                {t("dashboard.expense")}
                                            </CTableHeaderCell>

                                            {comparison.months.map((month) => (
                                                <CTableHeaderCell
                                                    className="text-end"
                                                    key={month.key}
                                                    scope="col"
                                                >
                                                    {month.label}
                                                </CTableHeaderCell>
                                            ))}
                                        </CTableRow>
                                    </CTableHead>

                                    <CTableBody>
                                        {comparison.expenses.map((expense) => (
                                            <CTableRow key={expense.id}>
                                                <CTableDataCell className="fw-semibold">
                                                    {expense.title}
                                                </CTableDataCell>

                                                {comparison.months.map((month) => (
                                                    <CTableDataCell className="text-end" key={month.key}>
                                                        {formatAmount(expense.amountsByMonth.get(month.key) || 0)}
                                                    </CTableDataCell>
                                                ))}
                                            </CTableRow>
                                        ))}
                                    </CTableBody>
                                </CTable>
                            )}
                        </>
                    )}
                </CCardBody>
            </CCard>
        </>
    );
}

function buildExpenseComparison(montlyReports, selectedYear) {
    const monthsByKey = new Map();
    const expensesById = new Map();

    montlyReports.filter((report) => Number(report.year) === selectedYear).forEach((report) => {
        const year = Number(report.year);
        const month = Number(report.month);
        const monthKey = `${year}-${String(month).padStart(2, "0")}`;

        monthsByKey.set(monthKey, {
            key: monthKey,
            label: monthKey,
            year,
            month,
        });

        report.expenses.forEach((expense) => {
            const amount = Number(expense.amount);

            if (!expensesById.has(expense.expenseId)) {
                expensesById.set(expense.expenseId, {
                    id: expense.expenseId,
                    title: expense.expenseTitle,
                    amountsByMonth: new Map(),
                });
            }

            const comparisonExpense = expensesById.get(expense.expenseId);

            comparisonExpense.amountsByMonth.set(
                monthKey,
                (comparisonExpense.amountsByMonth.get(monthKey) || 0) + amount
            );
        });
    });

    return {
        expenses: Array.from(expensesById.values()).sort((left, right) =>
            left.title.localeCompare(right.title)
        ),
        months: Array.from(monthsByKey.values()).sort(
            (left, right) => left.year - right.year || left.month - right.month
        ),
    };
}

function buildComparisonYears(montlyReports, currentYear) {
    return Array.from(
        new Set([
            currentYear,
            ...montlyReports.map((report) => Number(report.year)),
        ])
    ).sort((left, right) => right - left);
}
